import React from 'react';
import { select, t, Trans } from '@lingui/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import LandingSearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/LandingSearchResultsCarousel';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';

import { getSearchFiltersFromSeoLocationDetails } from '../../../utilities';
import {
  RoverProtectCondensed,
  SeoLocationFooter,
  SeoLocationHero,
  SeoLocationPageWrapper,
  ValuePropFilterOptions,
  WhyChooseRover,
} from '../../components';
import { PetSittingFaqs } from '../../components/Footer/ServiceFaqs';
import useFacts from '../../components/Footer/ServiceFaqs/useFacts';
import QuickFact, { QuickFactHighlight } from '../../components/QuickFact';
import SeoLocationReviewsCarousel from '../../components/SeoLocationReviewsCarousel';
import { PageSectionId, useSectionRef } from '../../hooks/useSectionRef';
import { petSittingHeroImage, quickFactBackgroundDoodle } from '../../images';
import SeoLocationPage, { SeoLocationPageProps } from '../../types';
import onValuePropFilterOptionsCtaClick from '../../utils/onValuePropFilterOptionsCtaClick';

export const PetSittingPage: SeoLocationPage = ({
  seoLocation,
  reviews,
  countryConfig,
  frontendConfig,
  serviceTypes,
  searchResponse,
  topNeighborhoodServices,
}: SeoLocationPageProps) => {
  const { language } = frontendConfig.settings;
  const initialSearchFilters = getSearchFiltersFromSeoLocationDetails(seoLocation);
  const valuePropSearchFilters = {
    ...initialSearchFilters,
    serviceType: ServiceType.OVERNIGHT_BOARDING,
  };
  const { localityType, details: seoLocationDetails, linkedLocations } = seoLocation;
  const { serviceTypeSlug, facts, name: locationName } = seoLocationDetails;
  const { localizedLinkUrls } = camelize(countryConfig.urls, true);

  const whyChooseRoverRef = useSectionRef(PageSectionId.WhyChooseRover);
  const faqsRef = useSectionRef(PageSectionId.FAQs);
  const howBrandWorksRef = useSectionRef(PageSectionId.HowBrandWorks);

  const {
    totalCustomersNum,
    medianListPricePerUnitLocalCurrencyNum,
    avgRepeatCustomerAmongSittersWithRepeatNum,
  } = useFacts(facts);

  // tokens for translations
  const cityName = locationName;
  const neighborhoodName = locationName;

  return (
    <SeoLocationPageWrapper seoLocation={seoLocation} countryConfig={countryConfig}>
      <Section>
        <Flex flexDirection="column" alignItems="center">
          <SeoLocationHero
            heading={seoLocationDetails.h1}
            subheading={seoLocationDetails.h2}
            desktopImage={petSittingHeroImage}
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
        </Flex>
      </Section>

      <RoverProtectCondensed countryConfig={countryConfig} my={['6x', '12x']} />

      <Section paddingLeft="4x" mb={['16x', '12x']}>
        <LandingSearchResultsCarousel
          searchResponse={searchResponse}
          searchFilters={initialSearchFilters}
          language={language}
          heading={select(localityType, {
            city: `Top rated ${cityName} pet sitters`,
            neighborhood: `Top rated ${neighborhoodName} pet sitters`,
            other: `Top rated ${locationName} pet sitters`,
          })}
          headingVariant="display"
          serviceTypeSlug={serviceTypeSlug}
          countryConfig={countryConfig}
        />
      </Section>

      {totalCustomersNum &&
        (() => {
          const { asNumber: ownerCount } = totalCustomersNum;
          return (
            <QuickFact stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                Over <QuickFactHighlight>{ownerCount}</QuickFactHighlight> pet parents in {cityName}{' '}
                have booked pet sitting
              </Trans>
            </QuickFact>
          );
        })()}

      <SeoLocationReviewsCarousel
        seoLocation={seoLocation}
        reviews={reviews}
        searchFilters={initialSearchFilters}
        language={language}
      />

      <ValuePropFilterOptions
        // 50% of bookings from Pet Sitting pages are for the Dog Boarding service type so search is re-routed
        locationName={locationName}
        language={language}
        searchFilters={valuePropSearchFilters}
        acceptsPuppies={false}
        onPress={onValuePropFilterOptionsCtaClick(serviceTypeSlug)}
      />

      <HowBrandWorks htmlRef={howBrandWorksRef} mt={['1x', '2x']} mb={['1x', '3x']}>
        <HowBrandWorks.Display>
          <Trans>How Rover works</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          {[
            {
              heading: t`Search`,
              content: t`Read verified reviews by pet parents like you and choose a sitter who’s a great match for you and your pets`,
            },
            {
              heading: t`Meet & Greet`,
              content: t`Meet either virtually or in-person with a sitter to discuss the level of care needed for your pets while you’re away`,
            },
            {
              heading: t`Book & Pay`,
              content: t`All payments are processed through Rover’s secured platform and your booking is backed by 24/7 support. It’s that easy!`,
            },
          ].map((item, index) => (
            <HowBrandWorks.ListItem key={index + 1}>
              <HowBrandWorks.ItemHeading step={index + 1}>{item.heading}</HowBrandWorks.ItemHeading>
              <HowBrandWorks.ItemContent>{item.content}</HowBrandWorks.ItemContent>
            </HowBrandWorks.ListItem>
          ))}
        </HowBrandWorks.List>
      </HowBrandWorks>

      {medianListPricePerUnitLocalCurrencyNum &&
        (() => {
          const { asCurrency: formattedAmount } = medianListPricePerUnitLocalCurrencyNum;
          return (
            <QuickFact stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                The median cost per night for pet sitting in {cityName} is{' '}
                <QuickFactHighlight>{formattedAmount}</QuickFactHighlight>
              </Trans>
            </QuickFact>
          );
        })()}

      <WhyChooseRover
        htmlRef={whyChooseRoverRef}
        reservationProtection={t`It doesn’t happen often, but if your pet sitter has to cancel, we’ll work with you to find a new sitter.`}
        supportText={t`We’re friendly, knowledgable, and total pet fanatics.`}
        supportUrl={countryConfig.urls.supportUrl}
      />
      {avgRepeatCustomerAmongSittersWithRepeatNum &&
        (() => {
          const { asNumber: clientCount } = avgRepeatCustomerAmongSittersWithRepeatNum;
          return (
            <QuickFact stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                Sitters in {cityName} average <QuickFactHighlight>{clientCount}</QuickFactHighlight>{' '}
                repeat pet sitting clients
              </Trans>
            </QuickFact>
          );
        })()}
      <Section>
        <PetSittingFaqs
          facts={facts}
          locationName={locationName}
          localityType={localityType}
          localizedLinkUrls={localizedLinkUrls}
          htmlRef={faqsRef}
        />
        <SeoLocationFooter
          countryConfigurationUrls={countryConfig.urls}
          seoLocation={seoLocationDetails}
          linkedLocations={linkedLocations}
          topNeighborhoodServices={topNeighborhoodServices}
        />
      </Section>
    </SeoLocationPageWrapper>
  );
};

export default PetSittingPage;
